/**
 * MCQ Element Grader
 */

import filter from 'lodash/filter';
import includes from 'lodash/includes';
import size from 'lodash/size';
import map from 'lodash/map';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import type {
    ElementGrader,
    GradingResult,
    ValidationResult,
    ValidationError,
} from '@ai-content-hub/content-kit';
import type { MCQElementData, MCQAnswer, MCQChoice } from './schema';
import { getQuestionText, getChoiceText } from './schema';

/** True when an MCQ answer holds a real selection (null/undefined/''/[] and blank strings do not). */
export function isMcqAnswered(answer: unknown): boolean {
    if (answer === undefined || answer === null) return false;
    if (Array.isArray(answer)) return answer.some((v) => typeof v === 'string' && v.trim() !== '');
    return String(answer).trim() !== '';
}

/**
 * MCQ Grader
 */
export const mcqGrader: ElementGrader<MCQElementData, MCQAnswer> = {
    /**
     * Grade the MCQ answer
     */
    grade(data: MCQElementData, answer: MCQAnswer, config = {}): GradingResult {
        const { max: maxPoints = 1, partialCredit = true } = config;
        const { choices, multipleCorrect, explanation } = data;

        // Get correct choice IDs
        const correctIds = map(
            filter(choices, (c: MCQChoice) => c.isCorrect),
            'id'
        );
        const totalCorrect = size(correctIds);

        if (totalCorrect === 0) {
            return {
                isCorrect: false,
                score: 0,
                max: maxPoints,
                feedback: 'This question has no correct answer set.',
            };
        }

        // Normalize answer to array
        const rawIds: string[] = isArray(answer) ? answer : answer ? [answer] : [];
        // Dedupe and drop ids that are not choices of this question: a repeated id must never
        // count twice (score above max) and an unknown id is not a selection.
        const choiceIds = map(choices, 'id');
        const selectedIds: string[] = multipleCorrect
            ? Array.from(new Set(rawIds)).filter((id) => includes(choiceIds, id))
            : rawIds;

        if (multipleCorrect) {
            // Multiple correct: check all selections
            const correctSelections = filter(selectedIds, (id: string) => includes(correctIds, id));
            const incorrectSelections = filter(
                selectedIds,
                (id: string) => !includes(correctIds, id)
            );

            const correctCount = size(correctSelections);
            const incorrectCount = size(incorrectSelections);

            // Perfect score: all correct selected, none incorrect
            const isPerfect = correctCount === totalCorrect && incorrectCount === 0;

            let score = 0;
            if (partialCredit) {
                // Partial credit: correct selections minus incorrect ones
                score = Math.min(
                    maxPoints,
                    Math.max(0, ((correctCount - incorrectCount) / totalCorrect) * maxPoints)
                );
            } else {
                score = isPerfect ? maxPoints : 0;
            }

            // Get feedback from selected choices
            const feedbackParts: string[] = [];
            selectedIds.forEach((id: string) => {
                const choice = find(choices, { id });
                if (choice?.feedback) {
                    feedbackParts.push(choice.feedback);
                }
            });

            return {
                isCorrect: isPerfect,
                score: Math.round(score * 100) / 100,
                max: maxPoints,
                feedback:
                    feedbackParts.length > 0
                        ? feedbackParts.join(' ')
                        : isPerfect
                          ? 'Correct!'
                          : `You selected ${correctCount} of ${totalCorrect} correct answers.`,
                correctAnswer: correctIds,
                details: {
                    correctSelections,
                    incorrectSelections,
                    explanation,
                },
            };
        } else {
            // Single correct: check the single selection
            const selectedId = selectedIds[0];
            const isCorrect = includes(correctIds, selectedId);

            // Get feedback from selected choice
            const selectedChoice = find(choices, { id: selectedId });
            const feedback =
                selectedChoice?.feedback || (isCorrect ? 'Correct!' : 'Incorrect. Try again.');

            return {
                isCorrect,
                score: isCorrect ? maxPoints : 0,
                max: maxPoints,
                feedback,
                correctAnswer: correctIds[0],
                details: {
                    explanation,
                    selectedChoice: selectedChoice ? getChoiceText(selectedChoice) : undefined,
                    correctChoice: (() => {
                        const correctChoice = find(choices, { id: correctIds[0] });
                        return correctChoice ? getChoiceText(correctChoice) : undefined;
                    })(),
                },
            };
        }
    },

    /**
     * Get the correct answer(s)
     */
    getCorrectAnswer(data: MCQElementData): MCQAnswer {
        const correctIds = map(
            filter(data.choices, (c: MCQChoice) => c.isCorrect),
            'id'
        );
        return data.multipleCorrect ? correctIds : correctIds[0];
    },

    /**
     * Validate MCQ data
     */
    validate(data: MCQElementData): ValidationResult {
        const errors: ValidationError[] = [];

        // Check question using helper
        const questionText = getQuestionText(data);
        if (!questionText || !questionText.trim()) {
            errors.push({
                field: 'question',
                message: 'Question is required',
                code: 'required',
            });
        }

        // Check choices
        if (!data.choices || size(data.choices) < 2) {
            errors.push({
                field: 'choices',
                message: 'At least 2 choices are required',
                code: 'minItems',
            });
        } else {
            // Check for at least one correct answer
            const hasCorrect = filter(data.choices, 'isCorrect').length > 0;
            if (!hasCorrect) {
                errors.push({
                    field: 'choices',
                    message: 'At least one choice must be marked as correct',
                    code: 'noCorrectAnswer',
                });
            }

            // Check that all choices have content
            data.choices.forEach((choice: MCQChoice, index: number) => {
                const choiceText = getChoiceText(choice);
                if (!choiceText || !choiceText.trim()) {
                    errors.push({
                        field: `choices.${index}.content`,
                        message: `Choice ${index + 1} content is required`,
                        code: 'required',
                    });
                }
            });
        }

        return {
            isValid: size(errors) === 0,
            errors,
        };
    },

    /**
     * MCQ is always 1 gradable question.
     */
    countQuestions(): number {
        return 1;
    },
};

export default mcqGrader;
